import React from 'react';
import { Languages, Printer } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';

const RECEIPT_TEXTS = {
  ar: {
    paymentReceipt: 'إيصال دفع',
    receiptNumber: 'رقم الإيصال',
    date: 'التاريخ',
    time: 'الوقت',
    barber: 'الحلاق',
    service: 'الخدمة',
    customer: 'العميل',
    amount: 'المبلغ',
    thanks: 'شكراً لزيارتكم',
    goodDay: 'نتمنى لكم يوماً سعيداً',
    print: 'تنزيل PDF',
    close: 'إغلاق',
    currency: 'د.إ',
    loyaltyTitle: 'برنامج الولاء',
    membership: 'رقم العضوية',
    originalAmount: 'المبلغ قبل الخصم',
    discount: 'خصم النقاط',
    netPaid: 'المبلغ المدفوع',
    pointsEarned: 'النقاط المكتسبة',
    pointsRedeemed: 'النقاط المستبدلة',
    pts: 'نقطة',
  },
  en: {
    paymentReceipt: 'Payment Receipt',
    receiptNumber: 'Receipt No.',
    date: 'Date',
    time: 'Time',
    barber: 'Barber',
    service: 'Service',
    customer: 'Customer',
    amount: 'Amount',
    thanks: 'Thank you for your visit',
    goodDay: 'Have a nice day',
    print: 'Download PDF',
    close: 'Close',
    currency: 'AED',
    loyaltyTitle: 'Loyalty Program',
    membership: 'Membership',
    originalAmount: 'Amount before discount',
    discount: 'Points discount',
    netPaid: 'Amount paid',
    pointsEarned: 'Points earned',
    pointsRedeemed: 'Points redeemed',
    pts: 'pts',
  },
};

const RevenueReceiptModal = ({
  receiptData,
  language,
  receiptLanguage,
  setReceiptLanguage,
  bilingualReceipt,
  setBilingualReceipt,
  receiptPrintRef,
  onPrint,
  onClose,
}) => {
  if (!receiptData) return null;

  const getReceiptText = (key) => {
    if (bilingualReceipt) return `${RECEIPT_TEXTS.ar[key]} / ${RECEIPT_TEXTS.en[key]}`;
    return RECEIPT_TEXTS[receiptLanguage]?.[key] || RECEIPT_TEXTS.ar[key];
  };

  const dir = bilingualReceipt ? 'rtl' : receiptLanguage === 'ar' ? 'rtl' : 'ltr';

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-card text-foreground p-4 rounded-lg max-w-md w-full space-y-4">
        {/* Language Options */}
        <div className="border border-white/10 rounded-lg p-3 space-y-3 print:hidden">
          <div className="flex items-center gap-2">
            <Languages className="w-4 h-4 text-primary" />
            <span className="font-medium text-sm">
              {language === 'ar' ? 'لغة الإيصال' : 'Receipt Language'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {language === 'ar' ? 'اختر اللغة' : 'Select Language'}
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setReceiptLanguage('ar')}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  receiptLanguage === 'ar' ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'
                }`}
              >عربي</button>
              <button
                type="button"
                onClick={() => setReceiptLanguage('en')}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  receiptLanguage === 'en' ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'
                }`}
              >English</button>
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <span className="text-sm">{language === 'ar' ? 'ثنائي اللغة' : 'Bilingual'}</span>
            <Switch checked={bilingualReceipt} onCheckedChange={setBilingualReceipt} />
          </div>
        </div>

        {/* Printable Receipt */}
        <div
          ref={receiptPrintRef}
          className="bg-white text-black p-6 rounded-lg print:shadow-none print:rounded-none"
          id="receipt-printable"
          dir={dir}
        >
          <div className="text-center border-b-2 border-dashed border-gray-300 pb-4 mb-4">
            <h2 className="text-2xl font-bold text-[#d4af37]">{receiptData.salon_name}</h2>
            <p className="text-sm text-gray-600 mt-1">{getReceiptText('paymentReceipt')}</p>
          </div>
          <div className="space-y-3 mb-4">
            <div className="flex justify-between"><span className="text-gray-600">{getReceiptText('receiptNumber')}:</span><span className="font-mono font-bold">{receiptData.receipt_number}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">{getReceiptText('date')}:</span><span>{receiptData.date}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">{getReceiptText('time')}:</span><span>{receiptData.time}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">{getReceiptText('barber')}:</span><span className="font-semibold">{receiptData.barber_name}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">{getReceiptText('service')}:</span><span>{receiptData.service_name}</span></div>
            {receiptData.customer_name && (
              <div className="flex justify-between"><span className="text-gray-600">{getReceiptText('customer')}:</span><span>{receiptData.customer_name}</span></div>
            )}
          </div>
          <div className="border-t-2 border-dashed border-gray-300 pt-4">
            <div className="flex justify-between text-xl font-bold">
              <span>{getReceiptText('amount')}:</span>
              <span className="text-[#d4af37]">{receiptData.amount} {bilingualReceipt ? 'د.إ / AED' : getReceiptText('currency')}</span>
            </div>
          </div>

          {/* Loyalty breakdown box */}
          {receiptData.loyalty && (receiptData.loyalty.redeem_points > 0 || receiptData.loyalty.points_earned > 0 || receiptData.loyalty.membership_number) && (
            <div className="mt-4 p-3 border-2 border-[#d4af37]/30 rounded-lg bg-[#fff8e7]">
              <div className="text-center mb-2">
                <span className="text-xs font-bold text-[#d4af37] uppercase tracking-wide">
                  {bilingualReceipt
                    ? `${RECEIPT_TEXTS.ar.loyaltyTitle} / ${RECEIPT_TEXTS.en.loyaltyTitle}`
                    : RECEIPT_TEXTS[receiptLanguage].loyaltyTitle}
                </span>
              </div>
              {receiptData.loyalty.membership_number && (
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600">{getReceiptText('membership')}:</span>
                  <span className="font-mono font-bold">#{receiptData.loyalty.membership_number}</span>
                </div>
              )}
              {receiptData.loyalty.redeem_points > 0 && (
                <>
                  {receiptData.loyalty.original_amount != null && (
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">{getReceiptText('originalAmount')}:</span>
                      <span className="font-mono">
                        {Number(receiptData.loyalty.original_amount).toFixed(2)}{' '}
                        {bilingualReceipt ? 'د.إ/AED' : getReceiptText('currency')}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs text-rose-600">
                    <span>− {getReceiptText('discount')} ({receiptData.loyalty.redeem_points} {getReceiptText('pts')}):</span>
                    <span className="font-mono">
                      −{Number(receiptData.loyalty.redeem_aed || 0).toFixed(2)}{' '}
                      {bilingualReceipt ? 'د.إ/AED' : getReceiptText('currency')}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs font-bold border-t border-dashed border-[#d4af37]/40 pt-1 mt-1">
                    <span>{getReceiptText('netPaid')}:</span>
                    <span className="font-mono">
                      {Number(receiptData.amount).toFixed(2)}{' '}
                      {bilingualReceipt ? 'د.إ/AED' : getReceiptText('currency')}
                    </span>
                  </div>
                </>
              )}
              {receiptData.loyalty.points_earned > 0 && (
                <div className="flex justify-between text-xs text-emerald-700 font-bold mt-1">
                  <span>✓ {getReceiptText('pointsEarned')}:</span>
                  <span>+{receiptData.loyalty.points_earned} {getReceiptText('pts')}</span>
                </div>
              )}
            </div>
          )}
          <div className="text-center mt-6 text-xs text-gray-500">
            <p>{getReceiptText('thanks')}</p>
            <p>{getReceiptText('goodDay')}</p>
          </div>
        </div>

        <div className="flex gap-3 print:hidden">
          <Button onClick={onPrint} className="flex-1 bg-[#d4af37] hover:bg-[#c9a030] text-black" data-testid="print-receipt-dialog-btn">
            <Printer className="w-4 h-4 ml-2" /> {getReceiptText('print')}
          </Button>
          <Button onClick={onClose} variant="outline" className="flex-1">{getReceiptText('close')}</Button>
        </div>
      </div>
    </div>
  );
};

export default RevenueReceiptModal;
export { RECEIPT_TEXTS };
